import { useState, useEffect, useCallback } from 'react'
import { Plus, Save, RefreshCw, Code, Table } from 'lucide-react'
import { fetchRules, updateRules } from '@/services/api'
import type { AccessRule, AccessRules } from '@/types/rules'
import RuleList from '@/components/RuleList'
import RuleEditDialog from '@/components/RuleEditDialog'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog'

export default function RulesPage() {
  const [rules, setRules] = useState<AccessRule[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editIndex, setEditIndex] = useState<number | null>(null)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null)

  // JSON edit mode
  const [jsonMode, setJsonMode] = useState(false)
  const [jsonText, setJsonText] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)

  const loadRules = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchRules()
      setRules(data.rules || [])
      setHasChanges(false)
    } catch (err) {
      setError('ルールの取得に失敗しました。')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRules()
  }, [loadRules])

  const handleSaveAll = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload: AccessRules = { rules }
      await updateRules(payload)
      setHasChanges(false)
    } catch (err) {
      setError('ルールの保存に失敗しました。')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  // Edit handlers
  const handleAddClick = () => {
    setEditIndex(null)
    setEditDialogOpen(true)
  }

  const handleEditClick = (index: number) => {
    setEditIndex(index)
    setEditDialogOpen(true)
  }

  const handleEditSave = (rule: AccessRule) => {
    setRules((prev) => {
      const next = [...prev]
      if (editIndex !== null) {
        next[editIndex] = rule
      } else {
        next.push(rule)
      }
      return next
    })
    setHasChanges(true)
    setEditDialogOpen(false)
  }

  // Move handlers
  const handleMove = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1
    if (target < 0 || target >= rules.length) return
    setRules((prev) => {
      const next = [...prev]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
    setHasChanges(true)
  }

  // Delete handlers
  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (deleteIndex !== null) {
      setRules((prev) => prev.filter((_, i) => i !== deleteIndex))
      setHasChanges(true)
    }
    setDeleteDialogOpen(false)
    setDeleteIndex(null)
  }

  // JSON mode toggle
  const handleToggleJsonMode = () => {
    if (!jsonMode) {
      setJsonText(JSON.stringify({ rules }, null, 2))
      setJsonError(null)
    } else {
      // Try to apply JSON changes
      try {
        const parsed = JSON.parse(jsonText)
        if (!parsed.rules || !Array.isArray(parsed.rules)) {
          setJsonError('JSONには "rules" 配列が必要です。')
          return
        }
        // Only mark as changed if JSON was actually modified
        const currentJson = JSON.stringify({ rules })
        const newJson = JSON.stringify(parsed)
        if (currentJson !== newJson) {
          setRules(parsed.rules)
          setHasChanges(true)
        }
        setJsonError(null)
      } catch {
        setJsonError('無効なJSONです。')
        return
      }
    }
    setJsonMode(!jsonMode)
  }

  const getDeleteMatchLabel = () => {
    if (deleteIndex === null) return ''
    const rule = rules[deleteIndex]
    return rule.match.email_domain
      ? `@${rule.match.email_domain}`
      : (rule.match.email ?? '')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">読み込み中...</span>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">アクセスルール</h2>
          <p className="text-sm text-gray-500 mt-1">
            ユーザーごとの権限ルールを管理します（上のルールが優先）
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleJsonMode}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title={jsonMode ? 'テーブル表示に切り替え' : 'JSON編集に切り替え'}
          >
            {jsonMode ? (
              <Table className="h-4 w-4" />
            ) : (
              <Code className="h-4 w-4" />
            )}
            {jsonMode ? 'テーブル' : 'JSON'}
          </button>
          <button
            onClick={loadRules}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title="再読み込み"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {!jsonMode && (
            <button
              onClick={handleAddClick}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              ルール追加
            </button>
          )}
          <button
            onClick={handleSaveAll}
            disabled={!hasChanges || saving}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            {saving ? '保存中...' : 'S3に保存'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Unsaved changes indicator */}
      {hasChanges && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
          未保存の変更があります。「S3に保存」ボタンで反映してください。
        </div>
      )}

      {/* Content */}
      {jsonMode ? (
        <div>
          {jsonError && (
            <div className="mb-2 text-sm text-red-600">{jsonError}</div>
          )}
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            className="w-full h-[500px] font-mono text-sm p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            spellCheck={false}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <RuleList
            rules={rules}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onMove={handleMove}
          />
        </div>
      )}

      {/* Dialogs */}
      <RuleEditDialog
        rule={editIndex !== null ? rules[editIndex] : null}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleEditSave}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        matchLabel={getDeleteMatchLabel()}
        onClose={() => {
          setDeleteDialogOpen(false)
          setDeleteIndex(null)
        }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
